import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { Dashboard, DashboardWidget, DateRangeConfig, GridPosition } from '@/types/dashboard'
import type { RootState } from '@/store'

export interface DashboardState {
  dashboards: Dashboard[]
  activeDashboardId: number | null
  isEditMode: boolean
  pendingLayout: GridPosition[]
  hasUnsavedChanges: boolean
  globalDateRange: DateRangeConfig
  globalFilters: {
    userIds?: number[]
  }
  editingWidgetId: number | null
  isLoadingWidgets: Record<string, boolean> // widgetId → loading
}

const initialState: DashboardState = {
  dashboards: [],
  activeDashboardId: null,
  isEditMode: false,
  pendingLayout: [],
  hasUnsavedChanges: false,
  globalDateRange: {
    field: '',
    mode: 'rolling',
    period: 'last_30_days',
  },
  globalFilters: {},
  editingWidgetId: null,
  isLoadingWidgets: {},
}

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDashboards: (state, { payload }: PayloadAction<Dashboard[]>) => {
      state.dashboards = payload
    },

    setActiveDashboard: (state, { payload }: PayloadAction<number | null>) => {
      state.activeDashboardId = payload
      // Exit edit mode when switching dashboards
      state.isEditMode = false
      state.pendingLayout = []
      state.hasUnsavedChanges = false
    },

    setEditMode: (state, { payload }: PayloadAction<boolean>) => {
      state.isEditMode = payload
      if (!payload) {
        // Discard pending layout when exiting edit mode
        state.pendingLayout = []
        state.hasUnsavedChanges = false
      }
    },

    setPendingLayout: (state, { payload }: PayloadAction<GridPosition[]>) => {
      state.pendingLayout = payload
      state.hasUnsavedChanges = true
    },

    markChangesSaved: (state) => {
      state.hasUnsavedChanges = false
      state.pendingLayout = []
    },

    setGlobalDateRange: (state, { payload }: PayloadAction<DateRangeConfig>) => {
      state.globalDateRange = payload
    },

    setGlobalFilters: (state, { payload }: PayloadAction<{ userIds?: number[] }>) => {
      state.globalFilters = payload
    },

    setEditingWidget: (state, { payload }: PayloadAction<number | null>) => {
      state.editingWidgetId = payload
    },

    updateWidgetInStore: (state, { payload }: PayloadAction<DashboardWidget>) => {
      const dash = state.dashboards.find(d => d.id === state.activeDashboardId)
      if (!dash) return
      const idx = dash.widgets.findIndex(w => w.id === payload.id)
      if (idx >= 0) {
        dash.widgets[idx] = payload
      }
    },

    addWidgetToStore: (state, { payload }: PayloadAction<DashboardWidget>) => {
      const dash = state.dashboards.find(d => d.id === state.activeDashboardId)
      if (dash) {
        dash.widgets.push(payload)
      }
    },

    removeWidgetFromStore: (state, { payload: widgetId }: PayloadAction<number>) => {
      const dash = state.dashboards.find(d => d.id === state.activeDashboardId)
      if (dash) {
        dash.widgets = dash.widgets.filter(w => w.id !== widgetId)
      }
    },

    setWidgetLoading: (state, { payload }: PayloadAction<{ widgetId: string; loading: boolean }>) => {
      if (payload.loading) {
        state.isLoadingWidgets[payload.widgetId] = true
      } else {
        delete state.isLoadingWidgets[payload.widgetId]
      }
    },

    updateDashboardInStore: (state, { payload }: PayloadAction<Partial<Dashboard> & { id: number }>) => {
      const idx = state.dashboards.findIndex(d => d.id === payload.id)
      if (idx >= 0) {
        state.dashboards[idx] = { ...state.dashboards[idx], ...payload }
      }
    },

    addDashboardToStore: (state, { payload }: PayloadAction<Dashboard>) => {
      state.dashboards.push(payload)
    },

    removeDashboardFromStore: (state, { payload: id }: PayloadAction<number>) => {
      state.dashboards = state.dashboards.filter(d => d.id !== id)
      if (state.activeDashboardId === id) {
        state.activeDashboardId = null
      }
    },

    setDefaultDashboardInStore: (state, { payload: id }: PayloadAction<number>) => {
      for (const d of state.dashboards) {
        if (d.layout) {
          d.layout.is_default = d.id === id
        }
      }
    },

    setMemberDefaultInStore: (state, { payload: id }: PayloadAction<number>) => {
      for (const d of state.dashboards) {
        d.is_member_default = d.id === id
      }
    },
  },
})

export const {
  setDashboards,
  setActiveDashboard,
  setEditMode,
  setPendingLayout,
  markChangesSaved,
  setGlobalDateRange,
  setGlobalFilters,
  setEditingWidget,
  updateWidgetInStore,
  addWidgetToStore,
  removeWidgetFromStore,
  setWidgetLoading,
  updateDashboardInStore,
  addDashboardToStore,
  removeDashboardFromStore,
  setDefaultDashboardInStore,
  setMemberDefaultInStore,
} = dashboardSlice.actions

// Selectors
export const selectDashboardState = (state: RootState) => state.dashboard
export const selectActiveDashboard = (state: RootState): Dashboard | null =>
  state.dashboard.dashboards.find(d => d.id === state.dashboard.activeDashboardId) ?? null
export const selectGlobalDateRange = (state: RootState) => state.dashboard.globalDateRange
export const selectGlobalFilters = (state: RootState) => state.dashboard.globalFilters
export const selectIsEditMode = (state: RootState) => state.dashboard.isEditMode
export const selectHasUnsavedChanges = (state: RootState) => state.dashboard.hasUnsavedChanges
